import { Component, type ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { ErrorOutline as ErrorIcon } from '@mui/icons-material';
import { colors, typography as t, radius } from '@/theme/tokens';
import { isChunkLoadError, reloadOnceForChunkError, resetChunkReloadGuard } from '@/lib/lazy-with-reload';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, componentStack: null };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('[ErrorBoundary] Caught render error:', error, info.componentStack);
    // Stale chunk after redeploy — auto-reload instead of stranding
    // the user on the generic error UI. Guarded against reload loops
    // inside reloadOnceForChunkError.
    if (isChunkLoadError(error) && reloadOnceForChunkError()) {
      return;
    }
    this.setState({ componentStack: info.componentStack ?? null });
  }

  private handleRetry = () => {
    // Stale chunk: re-mounting children will hit the same dead URL,
    // so do a real page reload instead. Reset the reload guard first
    // since this is an explicit user action.
    if (this.state.error && isChunkLoadError(this.state.error)) {
      resetChunkReloadGuard();
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null, componentStack: null });
  };

  private handleCopy = () => {
    const { error, componentStack } = this.state;
    if (!error) return;
    const text = [
      `Message: ${error.message}`,
      error.stack ? `\nStack:\n${error.stack}` : '',
      componentStack ? `\nComponent stack:${componentStack}` : '',
    ].join('');
    navigator.clipboard?.writeText(text).catch(() => {});
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            textAlign: 'center',
            gap: 2,
            p: 4,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: colors.status.offline.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ErrorIcon sx={{ fontSize: 32, color: colors.status.offline.color }} />
          </Box>
          <Typography sx={{ fontSize: t.fontSize.lg, fontWeight: t.fontWeight.semibold, color: 'text.primary' }}>
            Something went wrong
          </Typography>
          <Typography sx={{ fontSize: t.fontSize.base, color: 'text.secondary', maxWidth: 480 }}>
            An unexpected error occurred while loading this page. Try again or go back to the dashboard.
          </Typography>
          {this.state.error && (
            <Box
              sx={{
                mt: 1,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: `${radius.md}px`,
                maxWidth: 720,
                width: '100%',
                textAlign: 'left',
                overflow: 'auto',
                maxHeight: 320,
              }}
            >
              <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: 'error.main', whiteSpace: 'pre-wrap', fontWeight: t.fontWeight.semibold }}>
                {this.state.error.name}: {this.state.error.message}
              </Typography>
              {import.meta.env.DEV && this.state.error.stack && (
                <Typography
                  component="pre"
                  sx={{ fontSize: 11, fontFamily: 'monospace', color: 'text.secondary', whiteSpace: 'pre-wrap', mt: 1, mb: 0 }}
                >
                  {this.state.error.stack}
                </Typography>
              )}
              {import.meta.env.DEV && this.state.componentStack && (
                <Typography
                  component="pre"
                  sx={{ fontSize: 11, fontFamily: 'monospace', color: 'text.secondary', whiteSpace: 'pre-wrap', mt: 1, mb: 0 }}
                >
                  Component stack:{this.state.componentStack}
                </Typography>
              )}
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={this.handleGoHome}
              sx={{ borderColor: 'divider', color: 'text.secondary', borderRadius: `${radius.md}px`, textTransform: 'none' }}
            >
              Go to Dashboard
            </Button>
            {this.state.error && (
              <Button
                variant="outlined"
                onClick={this.handleCopy}
                sx={{ borderColor: 'divider', color: 'text.secondary', borderRadius: `${radius.md}px`, textTransform: 'none' }}
              >
                Copy error
              </Button>
            )}
            <Button
              variant="contained"
              onClick={this.handleRetry}
              sx={{ bgcolor: colors.primary.main, '&:hover': { bgcolor: colors.primary.hover }, borderRadius: `${radius.md}px`, textTransform: 'none' }}
            >
              Try Again
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}
